import React, { useImperativeHandle, forwardRef, useState, useRef } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { Assessment as AssessmentIcon } from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import VenueMapModal, { useVenueMapHandling } from './VenueMapModal';
import EventInfoCard from './shared/EventInfoCard';
import ScoringSection, { ScoringSectionHandle } from './shared/ScoringSection';
import HistorySection from './shared/HistorySection';
import SeatRecommendationComponent from './shared/SeatRecommendationComponent';
import {
    SingleEventAnalysisTabProps,
    SingleEventAnalysisTabHandle,
    ExtendedDataProvider,
    AIScoring
} from './utils/SingleEventAnalysisTab.types';
import { EventHistoryDashboard, EventItem } from '../../models/event';
import { getRecommendationIcon, getScoreColor } from '../../AnalysisPage';
import { AISeatAnalysisResult, SeatRecommendation } from '../../models/seat';

const REAL_TICKET_API_URL = process.env.REAL_TICKET_API_URL || 'https://localhost:5002';

/**
 * SingleEventAnalysisTab Component
 *
 * Container component that orchestrates child components for displaying
 * detailed analysis for a single event. Functions as a layout container
 * with minimal logic, delegating functionality to specialized child components.
 *
 * Child components:
 * - EventInfoCard: Displays event information
 * - ScoringSection: Manages AI scoring data and display
 * - HistorySection: Handles historical data and charts
 * - SeatRecommendationComponent: Manages seat recommendations
 *
 * @param props - Component props containing event data and analysis results
 * @param ref - React ref for exposing imperative methods to parent
 * @returns Single event analysis component
 */
const SingleEventAnalysisTab = forwardRef<SingleEventAnalysisTabHandle, SingleEventAnalysisTabProps>((props, ref) => {
    const {
        selectedEvent,
        setSelectedEvent,
        analysisCompleted,
        aiScoring,
        setAiScoring,
        seatRecommendations,
        setSeatRecommendations,
        preFilteringRules,
        setSearchResults
    } = props;

    // Loading state for Seat Recommendations
    const [isSeatRecommendationsLoading, setIsSeatRecommendationsLoading] = useState(false);

    const dataProvider = useDataProvider() as unknown as ExtendedDataProvider;
    const notify = useNotify();
    const aiScoringSectionRef = useRef<ScoringSectionHandle>(null);

    const { modalState, handleVenueMapClick, closeModal, handleImageError } = useVenueMapHandling();

    // Function to fetch scoring data for a given event
    const getAnalysisEventScore = async (event: EventItem): Promise<AIScoring> => {
        if (aiScoringSectionRef.current) {
            return await aiScoringSectionRef.current.fetchScoringData(event);
        }
        throw new Error('ScoringSection ref not available');
    };
    
    const getSeatRecommendations = async (event: EventItem | null, analysisId: number | 0): Promise<SeatRecommendation[]> => {
        const currentAnalysisId = analysisId;
        setIsSeatRecommendationsLoading(true);
        try {
            const postData = {
                // check event.id if number so convert to string to match format Kibana data
                eventId: event?.eventId,
                siteId: event?.siteId,
                type: event?.type?.trim(),
                venue: event?.venue,
                name: event?.name,
                url: event?.url,
                eventDate: event?.dateUtc ?? undefined,
                deliveryMethod: event?.deliveryMethod ?? undefined,
                liveTickets: event?.liveTickets ?? [],
                // Include venue ID for predefined venue layout lookup (with null safety)
                smVenueId: event?.venueId ?? undefined,
                
            };
            const analyzedSeats = await dataProvider.postData<SeatRecommendation[]>('real-ticket/TicketSelection', 'get-analyzed-seats', postData);

            if (!analyzedSeats || !analyzedSeats.data || analyzedSeats.data.length === 0) {
                console.warn('No analyzed seats found');
                return [];
            }

            const requestBody: any = currentAnalysisId
                ? { seats: analyzedSeats.data, analysisId: currentAnalysisId }
                : analyzedSeats.data;

            const response = await fetch(`${REAL_TICKET_API_URL}/recommend-seats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });
            if (!response.ok) {
                const respError = await response.json();
                throw new Error(`Real Ticket API error: ${response.status} ${respError.details}`);
            }
            const recommendSeats = await response.json();
            // Map response to existing SeatRecommendation structure
            const mappedSeats = recommendSeats.data.map((seat: any) => ({
                ...seat,
                purchaseStatus: analyzedSeats.data.find(s => s.seatId.toString() === seat.seatId.toString())?.purchaseStatus || seat.purchaseStatus,
            }));
            return mappedSeats as SeatRecommendation[];
        } catch (error) {
            notify(`Error fetching seat recommendations: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
            return [];
        } finally {
            setIsSeatRecommendationsLoading(false);
        }
    };
    /**
     * Exposes imperative methods to parent component for triggering data fetches
     */
    useImperativeHandle(ref, () => ({
        fetchScoringData: getAnalysisEventScore,
        fetchHistoricalScoringData: async (analysisId: number | bigint, target?: EventHistoryDashboard): Promise<AIScoring> => {
            if (aiScoringSectionRef.current) {
                return await aiScoringSectionRef.current.fetchHistoricalScoringData(analysisId, target);
            }
            throw new Error('ScoringSection ref not available');
        },
        fetchSeatRecommendations: getSeatRecommendations,
        // Get seat recommendations historical data
        getSeatRecommendationsHistory: async (analysisId: number | bigint): Promise<SeatRecommendation[]> => {
            setIsSeatRecommendationsLoading(true);
            try {
                // const postData = { analysisId: analysisId };
                const analyzedSeatsHistory = await dataProvider.fetchData<AISeatAnalysisResult[]>('HistoryAnalysis', `GetSeatAnalysisResults?analysisId=${analysisId}`);

                if (!analyzedSeatsHistory || !analyzedSeatsHistory.data || analyzedSeatsHistory.data.length === 0) {
                    console.warn('No analyzed seats found');
                    return [];
                }
                // convert AISeatAnalysisResult to SeatRecommendation
                let seatRecommendations = analyzedSeatsHistory.data.map(item => SeatRecommendation.fromSeatAnalysisResult(item));
                return seatRecommendations;
            } catch (error) {
                notify(`Error fetching seat recommendations: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
                return [];
            } finally {
                setIsSeatRecommendationsLoading(false);
            }
        }
    }), [dataProvider, notify]);

    if (!selectedEvent) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <AssessmentIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                    No Event Selected
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Search and select an event to see AI analysis
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Grid container spacing={2}>
                {/* Event Info and History Section */}
                <Grid size={5} spacing={2} paddingLeft={1}>
                    <Grid size={12}>
                        <EventInfoCard
                            aiScoring={aiScoring}
                            setSelectedEvent={setSelectedEvent}
                            selectedEvent={selectedEvent}
                            analysisCompleted={analysisCompleted}
                            onVenueMapClick={handleVenueMapClick}
                            setAiScoring={setAiScoring}
                            setSeatRecommendations={setSeatRecommendations}
                            getAnalysisEventScore={getAnalysisEventScore}
                            getSeatRecommendations={getSeatRecommendations}
                        />
                    </Grid>
                    <br />
                    <hr style={{ border: 0, borderTop: '1px solid rgba(255,255,255,0.2)' }} />
                    <HistorySection selectedEvent={selectedEvent} />
                </Grid>

                {/* AI Scoring Section */}
                <ScoringSection
                    ref={aiScoringSectionRef}
                    selectedEvent={selectedEvent}
                    setAiScoring={setAiScoring}
                    aiScoring={aiScoring}
                    getRecommendationIcon={getRecommendationIcon}
                    getScoreColor={getScoreColor}
                />

                {/* Seat Recommendations Section */}
                <SeatRecommendationComponent
                    analysisCompleted={analysisCompleted}
                    seatRecommendations={seatRecommendations}
                    setSeatRecommendations={setSeatRecommendations}
                    isSeatRecommendationsLoading={isSeatRecommendationsLoading}
                    preFilteringRules={preFilteringRules}
                    setAiScoring={setAiScoring}
                    aiScoring={aiScoring}
                    selectedEvent={selectedEvent}
                    setSearchResults={setSearchResults}
                />
            </Grid>

            {/* Modals */}
            <VenueMapModal modalState={modalState} onClose={closeModal} onImageError={handleImageError} />
        </>
    );
});

SingleEventAnalysisTab.displayName = 'SingleEventAnalysisTab';

export default SingleEventAnalysisTab;
export type { PrepareData } from './utils/SingleEventAnalysisTab.types';
